import React, { useState, useEffect } from 'react';
import { 
  Download, 
  FileText, 
  Filter, 
  Search,
  Users,
  BookOpen,
  ClipboardCheck,
  Gamepad2,
  Calendar,
  School,
  GraduationCap,
  RefreshCw
} from 'lucide-react';
import { adminExportService, ExportFilters, ExportData } from '../../services/admin/AdminExportService';
import { toast } from '../../utils/toast';
import { sqliteDatabaseService } from '../../services/database/SQLiteDatabaseService';

/**
 * Admin Statistics Export Page
 * Trang xuất thống kê và dữ liệu từ database
 */
const StatisticsExport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState<ExportData | null>(null);
  const [filters, setFilters] = useState<ExportFilters>({});
  const [showFilters, setShowFilters] = useState(true);

  // Filter states
  const [lessonId, setLessonId] = useState('');
  const [quizId, setQuizId] = useState('');
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'all' | 'student' | 'teacher' | 'admin'>('all');
  const [school, setSchool] = useState('');
  const [grade, setGrade] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await adminExportService.exportData(filters);
      setExportData(data);
    } catch (error) {
      console.error('Error loading export data:', error);
      toast.error('Không thể tải dữ liệu xuất');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const newFilters: ExportFilters = {};
    
    if (lessonId.trim()) newFilters.lessonId = lessonId.trim();
    if (quizId.trim()) newFilters.quizId = quizId.trim();
    if (userId.trim()) newFilters.userId = userId.trim();
    if (role !== 'all') newFilters.role = role;
    if (school.trim()) newFilters.school = school.trim();
    if (grade.trim()) newFilters.grade = grade.trim();
    if (startDate) newFilters.startDate = new Date(startDate);
    if (endDate) newFilters.endDate = new Date(endDate);
    
    setFilters(newFilters);
    loadData();
  };

  const resetFilters = () => {
    setLessonId('');
    setQuizId('');
    setUserId('');
    setRole('all');
    setSchool('');
    setGrade('');
    setStartDate('');
    setEndDate('');
    setFilters({});
    loadData();
  };

  const handleExport = async (type: 'users' | 'progress' | 'quizzes' | 'games' | 'all', format: 'csv' | 'json') => {
    if (!exportData) {
      toast.error('Chưa có dữ liệu để xuất');
      return;
    }

    try {
      setLoading(true);
      
      const timestamp = new Date().toISOString().split('T')[0];
      let filename = '';
      let content = '';

      if (format === 'csv') {
        content = adminExportService.exportToCSV(exportData, type);
        filename = `thong-ke-${type}-${timestamp}.csv`;
        adminExportService.downloadCSV(content, filename);
      } else {
        // For JSON, export specific type or all
        if (type === 'all') {
          content = adminExportService.exportToJSON(exportData);
        } else {
          const partialData: ExportData = {
            users: type === 'users' ? exportData.users : [],
            studyProgress: type === 'progress' ? exportData.studyProgress : [],
            quizResults: type === 'quizzes' ? exportData.quizResults : [],
            gameResults: type === 'games' ? exportData.gameResults : [],
            summary: exportData.summary,
          };
          content = adminExportService.exportToJSON(partialData);
        }
        filename = `thong-ke-${type}-${timestamp}.json`;
        adminExportService.downloadJSON(content, filename);
      }

      toast.success(`Đã xuất file ${filename}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Không thể xuất file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Xuất thống kê</h1>
            <p className="text-gray-600 mt-1">Xuất dữ liệu từ database theo các bộ lọc</p>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            <Filter className="w-5 h-5" />
            <span>{showFilters ? 'Ẩn bộ lọc' : 'Hiện bộ lọc'}</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Filter className="w-5 h-5" />
            <span>Bộ lọc</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Lesson ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BookOpen className="w-4 h-4 inline mr-1" />
                Bài học (Lesson ID)
              </label>
              <input
                type="text"
                value={lessonId}
                onChange={(e) => setLessonId(e.target.value)}
                placeholder="VD: lop-11-chuong-2-song-bai-6"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Quiz ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ClipboardCheck className="w-4 h-4 inline mr-1" />
                Trắc nghiệm (Quiz ID)
              </label>
              <input
                type="text"
                value={quizId}
                onChange={(e) => setQuizId(e.target.value)}
                placeholder="VD: quiz-lop-11-chuong-2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* User ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                User ID
              </label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="ID người dùng"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <GraduationCap className="w-4 h-4 inline mr-1" />
                Vai trò
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tất cả</option>
                <option value="student">Học sinh</option>
                <option value="teacher">Giáo viên</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* School */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <School className="w-4 h-4 inline mr-1" />
                Trường học
              </label>
              <input
                type="text"
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                placeholder="Tên trường"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Grade/Class */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <GraduationCap className="w-4 h-4 inline mr-1" />
                Lớp
              </label>
              <input
                type="text"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                placeholder="VD: 10A1, 11, 12"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Từ ngày
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Đến ngày
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center space-x-3 mt-6">
            <button
              onClick={applyFilters}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-5 h-5" />
              <span>Áp dụng bộ lọc</span>
            </button>
            <button
              onClick={resetFilters}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-5 h-5" />
              <span>Đặt lại</span>
            </button>
          </div>
        </div>
      )}

      {/* Summary */}
      {exportData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tổng người dùng</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{exportData.summary.totalUsers}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Học sinh</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{exportData.summary.totalStudents}</p>
              </div>
              <GraduationCap className="w-10 h-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tiến độ học tập</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{exportData.summary.totalStudyProgress}</p>
              </div>
              <BookOpen className="w-10 h-10 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Kết quả trắc nghiệm</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{exportData.summary.totalQuizResults}</p>
              </div>
              <ClipboardCheck className="w-10 h-10 text-orange-500" />
            </div>
          </div>
        </div>
      )}

      {/* Export Options */}
      {exportData && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
            <Download className="w-5 h-5" />
            <span>Xuất dữ liệu</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Export Users */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <Users className="w-5 h-5" />
                <span>Người dùng ({exportData.summary.totalUsers})</span>
              </h3>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => handleExport('users', 'csv')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport('users', 'json')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>

            {/* Export Progress */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Tiến độ ({exportData.summary.totalStudyProgress})</span>
              </h3>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => handleExport('progress', 'csv')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport('progress', 'json')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>

            {/* Export Quizzes */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <ClipboardCheck className="w-5 h-5" />
                <span>Trắc nghiệm ({exportData.summary.totalQuizResults})</span>
              </h3>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => handleExport('quizzes', 'csv')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport('quizzes', 'json')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>

            {/* Export Games */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <Gamepad2 className="w-5 h-5" />
                <span>Game ({exportData.summary.totalGameResults})</span>
              </h3>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => handleExport('games', 'csv')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport('games', 'json')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>

            {/* Export All */}
            <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center space-x-2">
                <Download className="w-5 h-5" />
                <span>Tất cả dữ liệu</span>
              </h3>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => handleExport('all', 'csv')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleExport('all', 'json')}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  <span>JSON</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Đang tải dữ liệu...</span>
        </div>
      )}
    </div>
  );
};

export default StatisticsExport;

